import {
  Box,
  HStack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { setUpTOTP } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { CustomToast, ToastType } from 'src/components/atoms/CustomToast';
import { Icon } from 'src/components/atoms/Icon';
import { useAuthContext } from 'src/contexts/AuthContext';
import {
  useMfaPreferenceQuery,
  useInvalidateMfaPreference,
} from 'src/hooks/useMfaPreference';
import { TotpSetupModal } from './TotpSetupModal';

/**
 * Settings section for the authenticator app (TOTP) MFA method.
 */
export const AuthenticatorAppSection: React.FC = () => {
  const { userProfile } = useAuthContext();
  const { data: mfaPreference, isLoading } = useMfaPreferenceQuery();
  const invalidateMfaPreference = useInvalidateMfaPreference();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [setupUri, setSetupUri] = useState<string | null>(null);
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [isStartingSetup, setIsStartingSetup] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastTitle, setToastTitle] = useState('');
  const [toastDescription, setToastDescription] = useState('');
  const [toastType, setToastType] = useState<ToastType>('success');

  const textColor = useColorModeValue('gray.800', 'white');

  const isTotpEnabled = Boolean(mfaPreference?.enabled?.includes('TOTP'));

  const showCustomToast = (
    title: string,
    description: string,
    type: ToastType,
  ) => {
    setToastTitle(title);
    setToastDescription(description);
    setToastType(type);
    setShowToast(true);
  };

  const handleOpenSetup = async () => {
    try {
      setIsStartingSetup(true);
      const totpSetupDetails = await setUpTOTP();
      setSetupSecret(totpSetupDetails.sharedSecret);
      setSetupUri(
        totpSetupDetails.getSetupUri('Merkatus', userProfile?.email).toString(),
      );
      onOpen();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error starting TOTP setup:', error);
      showCustomToast(
        'Error starting setup',
        'Please try again or contact support.',
        'error',
      );
    } finally {
      setIsStartingSetup(false);
    }
  };

  const handleVerified = () => {
    invalidateMfaPreference();
    onClose();
    showCustomToast(
      'Authenticator app configured!',
      'You can now use it to sign in.',
      'success',
    );
  };

  return (
    <Box width="100%" maxWidth={'460px'}>
      <Text fontSize="md" fontWeight="semibold" color={textColor} mb={2}>
        Two-Factor Authentication
      </Text>
      <Text fontSize="sm" color="gray.500" mb={6}>
        Email verification codes are always enabled for your account. You can
        additionally use an authenticator app (e.g. Google Authenticator or
        Microsoft Authenticator) to sign in.
      </Text>

      {isTotpEnabled && (
        <HStack mb={4} spacing={2}>
          <Icon iconName="check" boxSize={4} color="icon.green" />
          <Text fontSize="sm" fontWeight="medium" color={textColor}>
            Authenticator app is configured
          </Text>
        </HStack>
      )}

      <Button
        variant="primary"
        size={{ base: 'md', md: 'lg' }}
        px={{ base: 8, md: 16 }}
        py={{ base: 4, md: 6 }}
        width="100%"
        buttonName={
          isTotpEnabled ? 'change-authenticator-app' : 'setup-authenticator-app'
        }
        onClick={handleOpenSetup}
        isLoading={isStartingSetup}
        disabled={isLoading || isStartingSetup}>
        {isTotpEnabled
          ? 'Use a different authenticator app'
          : 'Set up authenticator app'}
      </Button>

      <TotpSetupModal
        isOpen={isOpen}
        onClose={onClose}
        header={
          isTotpEnabled
            ? 'Change authenticator app'
            : 'Set up authenticator app'
        }
        setupUri={setupUri}
        setupSecret={setupSecret}
        onVerified={handleVerified}
      />

      <CustomToast
        isOpen={showToast}
        title={toastTitle}
        description={toastDescription}
        type={toastType}
        duration={3000}
        onClose={() => setShowToast(false)}
      />
    </Box>
  );
};
